/**
 * N2CalendarReadPage — "Calendar Read of the Market."
 *
 * Lives at /n2/calendar-notches. Unlike the rest of /n2 (public marketing),
 * this page is Premier-gated: the read API (GET /n2/api/calendar-notches/read)
 * requires a Bearer JWT AND `calendarNotchesEnabled === true` on the user
 * record (see api/auth/src/notchesGetCalendarRead.js). The nav link in
 * N2Frame is hidden for non-enabled users, but this page still has to handle
 * a direct hit from an unauthenticated or non-entitled visitor gracefully.
 *
 * Renders: hero card (confidence-colored border) -> events driving the read
 * -> commentary markdown -> illustrative /ES calendar structure (disclaimer,
 * not a trade recommendation) -> recent-reads table. `today: null` is a
 * valid response (no mispricing detected today), not an error -- shown as a
 * quiet grey card per feedback-no-signals-is-valid.md.
 */

import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede, GoldAccentCard, Body } from './N2Primitives';
import { useAuth } from '../contexts/AuthContext';
import {
  notchesCalendarReadService,
  CalendarReadResponse,
  CalendarReadRow,
} from '../services/notchesCalendarReadService';

const TIER_COLOR: Record<string, string> = {
  HIGH: palette.positive,
  MEDIUM: palette.caution,
  LOW: palette.ink3,
  NO_SIGNAL: palette.ink3,
};

const IMPACT_COLOR: Record<string, string> = {
  high: palette.negative,
  moderate: palette.caution,
  low: palette.ink3,
};

export const N2CalendarReadPage: React.FC = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [data, setData] = useState<CalendarReadResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    notchesCalendarReadService
      .getRead(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          setErr('Calendar Read isn’t included on your plan yet.');
        } else if (status === 401) {
          setErr('Your session expired. Please sign in again.');
        } else {
          setErr(e?.message || 'Failed to load the calendar read.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, token]);

  return (
    <N2Frame page="calendar-notches">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px 80px' }}>
        <Eyebrow>Calendar Read</Eyebrow>
        <PageTitle italic="not tips.">
          Calendar read of the market,
        </PageTitle>
        <PageLede>
          A daily read on term-structure mispricing in the futures options
          curve -- what's driving it, and how it would translate into a
          calendar structure if you were building one.
        </PageLede>

        {!isAuthenticated && <SignInPrompt />}
        {isAuthenticated && loading && <LoadingCard />}
        {isAuthenticated && !loading && err && <ErrorCard message={err} />}
        {isAuthenticated && !loading && !err && data && (
          <>
            <TodayCard row={data.today} />
            <RecentReadsTable rows={data.recent} />
          </>
        )}
      </div>
    </N2Frame>
  );
};

const SignInPrompt: React.FC = () => (
  <GoldAccentCard>
    <div style={{ fontFamily: 'var(--sans)', fontSize: 15, color: palette.ink2, lineHeight: 1.6 }}>
      Sign in with a Premier account to see today's calendar read.
    </div>
    <div style={{ marginTop: 16 }}>
      <Link
        to={`/login?redirect=${encodeURIComponent('n2/calendar-notches')}`}
        style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600, color: palette.goldDeep }}
      >Sign in →</Link>
    </div>
  </GoldAccentCard>
);

const LoadingCard: React.FC = () => (
  <div style={{ marginTop: 24, padding: '40px 0', textAlign: 'center' }}>
    <Body>Loading today's read…</Body>
  </div>
);

const ErrorCard: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    marginTop: 24, padding: '20px 24px',
    border: `1px solid ${palette.line}`, background: palette.paper2,
    borderRadius: 3,
  }}>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.negative, marginBottom: 8,
    }}>Calendar Read unavailable</div>
    <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3, lineHeight: 1.6 }}>
      {message}
    </div>
  </div>
);

const TodayCard: React.FC<{ row: CalendarReadRow | null }> = ({ row }) => {
  if (!row) {
    return (
      <div style={{
        marginTop: 8, marginBottom: 40, padding: '24px 28px',
        border: `1px solid ${palette.line}`, borderRadius: 4,
        background: palette.paper2, color: palette.ink3,
        fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.6,
      }}>
        No calendar mispricing detected today. Term structure fitting normally.
      </div>
    );
  }

  const tierColor = TIER_COLOR[row.confidence?.tier] || palette.ink3;

  return (
    <div style={{
      marginTop: 8, marginBottom: 40,
      border: `2px solid ${tierColor}`, borderRadius: 4,
      background: palette.paper, padding: '28px 32px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          padding: '4px 10px', borderRadius: 3,
          background: `${tierColor}18`, color: tierColor,
        }}>{row.confidence?.tier || 'UNKNOWN'}</span>
        <span style={{ fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3 }}>
          {row.generated_at ? new Date(row.generated_at).toLocaleString() : row.date}
        </span>
      </div>

      <h2 style={{
        fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 26,
        lineHeight: 1.3, color: palette.ink, margin: '0 0 20px',
      }}>{row.hero_summary}</h2>

      {row.events?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <SectionEyebrow>Events driving this pair</SectionEyebrow>
          {row.events.map((e, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'baseline', gap: 8,
              fontFamily: 'var(--sans)', fontSize: 14, color: palette.ink2,
              marginBottom: 4,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: IMPACT_COLOR[e.impact] || palette.ink3,
              }} />
              <strong style={{ color: palette.ink }}>{e.name}</strong>
              <span style={{ color: palette.ink3 }}>· {e.event_ts}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: row.trade_illustration ? 20 : 0 }}>
        <SectionEyebrow>Read of the market</SectionEyebrow>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.7, color: palette.ink2,
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{row.commentary_markdown}</ReactMarkdown>
        </div>
      </div>

      {row.trade_illustration && (
        <div style={{ borderTop: `1px solid ${palette.line}`, paddingTop: 20 }}>
          <SectionEyebrow>If you were structuring this on /ES</SectionEyebrow>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: palette.ink2, marginBottom: 4 }}>
            <strong style={{ color: palette.negative }}>SHORT</strong>{'  '}
            /ES {row.trade_illustration.short_leg.expiration} {row.trade_illustration.short_leg.strike}C
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: palette.ink2, marginBottom: 8 }}>
            <strong style={{ color: palette.positive }}>LONG</strong>{'   '}
            /ES {row.trade_illustration.long_leg.expiration} {row.trade_illustration.long_leg.strike}C
          </div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3, marginBottom: 4 }}>
            {(() => {
              const g = new Date(row.generated_at);
              const stamp = g.toLocaleString('en-US', {
                timeZone: 'America/New_York',
                month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit',
              });
              const spot = typeof row.trade_illustration!.es_spot === 'number'
                ? row.trade_illustration!.es_spot.toFixed(2)
                : row.trade_illustration!.atm_ref.toFixed(0);
              return `As of ${stamp} ET · /ES was ${spot} · strikes snap to what was ATM then, revalidate against current chain.`;
            })()}
          </div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3, fontStyle: 'italic' }}>
            Illustrative structural translation only — not a trade recommendation. Do your own sizing.
          </div>
        </div>
      )}

      {row.signal_source && (
        <div style={{ marginTop: 16, fontFamily: 'var(--mono)', fontSize: 11, color: palette.ink3 }}>
          Under the hood: {row.signal_source.symbol} {row.signal_source.pair?.t1}→{row.signal_source.pair?.t2}
          {typeof row.signal_source.deviation_vol_pts === 'number' && (
            <> · Δ {row.signal_source.deviation_vol_pts.toFixed(2)} vol pts</>
          )}
          {row.signal_source.side && <> · {row.signal_source.side}</>}
        </div>
      )}
    </div>
  );
};

const SectionEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.20em', textTransform: 'uppercase',
    color: palette.ink3, marginBottom: 10,
  }}>{children}</div>
);

const RecentReadsTable: React.FC<{ rows: CalendarReadRow[] }> = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.ink3, paddingBottom: 12,
        borderBottom: `1px solid ${palette.line}`, marginBottom: 4,
      }}>Recent reads</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: 13 }}>
        <tbody>
          {rows.map((r) => {
            const tierColor = TIER_COLOR[r.confidence?.tier] || palette.ink3;
            return (
              <tr key={r.commentaryId} style={{ borderBottom: `1px solid ${palette.line}` }}>
                <td style={{ padding: '10px 6px', color: palette.ink3, whiteSpace: 'nowrap' }}>{r.date}</td>
                <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.10em',
                    textTransform: 'uppercase', color: tierColor,
                  }}>{r.confidence?.tier || 'UNKNOWN'}</span>
                </td>
                <td style={{ padding: '10px 6px', color: palette.ink2 }}>{r.hero_summary}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default N2CalendarReadPage;
